import { visionLabApi } from '@/app/apiClient';
import {
  fieldLogSessionsRequestSchema,
  fieldLogVisitsRequestSchema,
} from '@/features/field-log/lib/requestContracts';
import type {
  CreateFieldLogSessionsBatchInput,
  CreateFieldLogVisitInput,
  DeleteEmptyFieldLogVisitResponse,
  DeleteFieldLogSessionsBatchInput,
  FieldLogBatchResult,
  FieldLogFootageCandidatesResponse,
  FieldLogFootageChangeInput,
  FieldLogFootageChangeResponse,
  FieldLogFootageLinkLookupResponse,
  FieldLogFootageLinksResponse,
  FieldLogFootageLookupRequest,
  FieldLogFootageLookupResponse,
  FieldLogFootageReviewInput,
  FieldLogFootageReviewResponse,
  FieldLogOrganizationOperation,
  FieldLogOrganizationOptionsPage,
  FieldLogSession,
  FieldLogSessionsPage,
  FieldLogSessionsRequest,
  FieldLogVisit,
  FieldLogVisitDetail,
  FieldLogVisitQuery,
  FieldLogVisitsPage,
  FieldLogVisitsRequest,
  FootageCandidatesQuery,
  PatchFieldLogSessionInput,
  PatchFieldLogSessionsBatchInput,
  PatchFieldLogVisitInput,
  PatchFieldLogVisitsBatchInput,
  SessionId,
  StartFieldLogSessionInput,
  VisitId,
} from '@/features/field-log/types/fieldLog';
import type { ApiCallOptions } from '@/shared/api/client';

/**
 * Visits are addressed under `field-log-visits`. A Field Log Session is *started*
 * under its visit but every later operation addresses it by its own `SES-` id at
 * the top level, so the two paths are separate.
 */
const VISITS_PATH = '/field-log-visits';
const SESSIONS_PATH = '/field-log-sessions';
const FOOTAGE_LINKS_PATH = '/field-log-footage-links';
const FOOTAGE_LOOKUP_PATH = '/field-log-footage/lookup';
const ORGANIZATION_OPTIONS_PATH = '/field-log-organization-options';

function visitPath(visitId: VisitId): string {
  return `${VISITS_PATH}/${encodeURIComponent(visitId)}`;
}

function sessionPath(sessionId: SessionId): string {
  return `${SESSIONS_PATH}/${encodeURIComponent(sessionId)}`;
}

export async function fetchFieldLogVisits(
  params: FieldLogVisitsRequest = {},
  options: ApiCallOptions = {},
): Promise<FieldLogVisitsPage> {
  fieldLogVisitsRequestSchema.parse(params);
  return visionLabApi.get<FieldLogVisitsPage>(VISITS_PATH, {
    ...options,
    searchParams: {
      cursor: params.cursor,
      limit: params.limit,
      createBy: params.createBy,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      timeZone: params.timeZone,
      startedAtFrom: params.startedAtFrom,
      startedAtTo: params.startedAtTo,
      createdAtFrom: params.createdAtFrom,
      createdAtTo: params.createdAtTo,
      venueName: params.venueName,
      cameraOpsCode: params.cameraOpsCode,
      hasSessions: params.hasSessions,
      footageStatus: params.footageStatus,
      operatorPresence: params.operatorPresence,
      recording: params.recording,
      organizationId: params.organizationId,
      projectId: params.projectId,
      footageMatch: params.footageMatch,
      toleranceMinutes: params.toleranceMinutes,
      include: params.include,
    },
  });
}

export function fetchFieldLogOrganizationOptions(
  params: {
    operation: FieldLogOrganizationOperation;
    cursor?: string;
    limit?: number;
  },
  options: ApiCallOptions = {},
): Promise<FieldLogOrganizationOptionsPage> {
  return visionLabApi.get<FieldLogOrganizationOptionsPage>(ORGANIZATION_OPTIONS_PATH, {
    ...options,
    searchParams: params,
  });
}

/** Visit detail carries the `cameras` grouping used to restore recording state. */
export async function fetchFieldLogVisit(
  visitId: VisitId,
  query: FieldLogVisitQuery = {},
  options: ApiCallOptions = {},
): Promise<FieldLogVisitDetail> {
  return visionLabApi.get<FieldLogVisitDetail>(visitPath(visitId), {
    ...options,
    searchParams: {
      include: query.include,
      organizationId: query.organizationId,
      projectId: query.projectId,
      toleranceMinutes: query.toleranceMinutes,
    },
  });
}

export async function createFieldLogVisit(input: CreateFieldLogVisitInput): Promise<FieldLogVisit> {
  return visionLabApi.post<FieldLogVisit>(VISITS_PATH, { json: input });
}

export async function patchFieldLogVisit(
  visitId: VisitId,
  input: PatchFieldLogVisitInput,
): Promise<FieldLogVisit> {
  return visionLabApi.patch<FieldLogVisit>(visitPath(visitId), { json: input });
}

/** The server rejects visits that still contain an undeleted session. */
export async function deleteEmptyFieldLogVisit(
  visitId: VisitId,
): Promise<DeleteEmptyFieldLogVisitResponse> {
  return visionLabApi.post<DeleteEmptyFieldLogVisitResponse>(`${visitPath(visitId)}/delete-empty`, {
    json: {},
  });
}

/** Starts a recording on one camera of this visit; the server stamps `startedAt`. */
export async function startFieldLogSession(
  visitId: VisitId,
  input: StartFieldLogSessionInput,
): Promise<FieldLogSession> {
  return visionLabApi.post<FieldLogSession>(`${visitPath(visitId)}/sessions`, { json: input });
}

export async function patchFieldLogSession(
  sessionId: SessionId,
  input: PatchFieldLogSessionInput,
): Promise<FieldLogSession> {
  return visionLabApi.patch<FieldLogSession>(sessionPath(sessionId), { json: input });
}

/** Idempotent: an already-ended session keeps its original `endedAt`. */
export async function endFieldLogSession(
  sessionId: SessionId,
  endedAt: string,
): Promise<FieldLogSession> {
  return visionLabApi.post<FieldLogSession>(`${sessionPath(sessionId)}/end`, { json: { endedAt } });
}

export async function deleteFieldLogSession(sessionId: SessionId): Promise<FieldLogSession> {
  return visionLabApi.delete<FieldLogSession>(sessionPath(sessionId));
}

export function batchCreateFieldLogSessions(
  visitId: string,
  input: CreateFieldLogSessionsBatchInput,
) {
  return visionLabApi.post<FieldLogBatchResult>(
    `${VISITS_PATH}/${encodeURIComponent(visitId)}/sessions/batch/create`,
    {
      json: input,
    },
  );
}

export function batchPatchFieldLogSessions(input: PatchFieldLogSessionsBatchInput) {
  return visionLabApi.post<FieldLogBatchResult>(`${SESSIONS_PATH}/batch/patch`, { json: input });
}

export function batchPatchFieldLogVisits(input: PatchFieldLogVisitsBatchInput) {
  return visionLabApi.post<FieldLogBatchResult>(`${VISITS_PATH}/batch/patch`, { json: input });
}

export function batchDeleteFieldLogSessions(input: DeleteFieldLogSessionsBatchInput) {
  return visionLabApi.post<FieldLogBatchResult>(`${SESSIONS_PATH}/batch/delete`, { json: input });
}

export async function fetchFieldLogFootage(
  sessionId: SessionId,
  options: ApiCallOptions = {},
): Promise<FieldLogFootageLinksResponse> {
  return visionLabApi.get<FieldLogFootageLinksResponse>(
    `${sessionPath(sessionId)}/footage`,
    options,
  );
}

export function putFieldLogFootageReview(
  sessionId: SessionId,
  input: FieldLogFootageReviewInput,
): Promise<FieldLogFootageReviewResponse> {
  return visionLabApi.put<FieldLogFootageReviewResponse>(
    `${sessionPath(sessionId)}/footage-review`,
    { json: input },
  );
}

/** Returns the current Field Log owner for a bounded batch of readable assets. */
export async function fetchFieldLogFootageLinks(
  assetIds: readonly string[],
  options: ApiCallOptions = {},
): Promise<FieldLogFootageLinkLookupResponse> {
  return visionLabApi.get<FieldLogFootageLinkLookupResponse>(FOOTAGE_LINKS_PATH, {
    ...options,
    searchParams: { assetId: assetIds },
  });
}

/** Searches target Sessions for asset-side linking. Callers control the cursor verbatim. */
export async function fetchFieldLogSessions(
  params: FieldLogSessionsRequest = {},
  options: ApiCallOptions = {},
): Promise<FieldLogSessionsPage> {
  fieldLogSessionsRequestSchema.parse(params);
  return visionLabApi.get<FieldLogSessionsPage>(SESSIONS_PATH, {
    ...options,
    searchParams: {
      cursor: params.cursor,
      limit: params.limit,
      organizationId: params.organizationId,
      projectId: params.projectId,
      runner: params.runner,
      environmentId: params.environmentId,
      environmentPresence: params.environmentPresence,
      operatorPresence: params.operatorPresence,
      startedAtFrom: params.startedAtFrom,
      startedAtTo: params.startedAtTo,
      opsCode: params.opsCode,
      operatorId: params.operatorId,
      recording: params.recording,
      linked: params.linked,
      footageMatch: params.footageMatch,
      footageStatus: params.footageStatus,
      include: params.include,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      timeZone: params.timeZone,
      q: params.q?.trim() || undefined,
    },
  });
}

export async function fetchFieldLogFootageCandidates(
  sessionId: SessionId,
  query: FootageCandidatesQuery = {},
  options: ApiCallOptions = {},
): Promise<FieldLogFootageCandidatesResponse> {
  return visionLabApi.get<FieldLogFootageCandidatesResponse>(
    `${sessionPath(sessionId)}/footage-candidates`,
    {
      ...options,
      searchParams: {
        organizationId: query.organizationId,
        projectId: query.projectId,
        toleranceMinutes: query.toleranceMinutes,
        include: query.include,
      },
    },
  );
}

/** Looks up one or more exact filenames in the caller's readable asset scope. */
export async function fetchFieldLogFootageLookup(
  input: FieldLogFootageLookupRequest,
  options: ApiCallOptions = {},
): Promise<FieldLogFootageLookupResponse> {
  return visionLabApi.post<FieldLogFootageLookupResponse>(FOOTAGE_LOOKUP_PATH, {
    ...options,
    json: input,
  });
}

/** Atomically adds or moves footage onto this session with optimistic ownership checks. */
export async function changeFieldLogFootage(
  sessionId: SessionId,
  input: FieldLogFootageChangeInput,
): Promise<FieldLogFootageChangeResponse> {
  return visionLabApi.post<FieldLogFootageChangeResponse>(
    `${sessionPath(sessionId)}/footage/change`,
    {
      json: input,
    },
  );
}

/** Removes an existing footage link; an absent link is reported as a conflict. */
export async function unlinkFieldLogFootage(
  sessionId: SessionId,
  assetId: string,
): Promise<{ ok: true }> {
  return visionLabApi.delete<{ ok: true }>(
    `${sessionPath(sessionId)}/footage/${encodeURIComponent(assetId)}`,
  );
}
